import { createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import { currentUser } from '../lib/auth-store.js';
import { toast } from '../lib/toast.js';
import {
  connectionsListView, setConnectionsListView,
  connectionsCollapsedGroups, setConnectionsCollapsedGroups,
} from '../lib/prefs.js';
import ConnectionForm from '../components/ConnectionForm.jsx';

export default function Connections(props) {
  const [items, { refetch }] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections;
  });

  const [editing, setEditing] = createSignal(null); // null | 'new' | {...connection}
  const [busy, setBusy]       = createSignal(null); // connection id being tested/deleted

  // ── Reachability status (online/offline indicator) ──
  // One batched, bounded-parallel probe (SELECT 1 per connection) on the server;
  // the dots resolve when it returns. Auto-runs once when the list first loads;
  // the ↻ Recheck button re-probes on demand. Status is point-in-time, not live.
  const [statuses, setStatuses] = createSignal({});  // id -> { state, error }
  const [checking, setChecking] = createSignal(false);
  const [hideOffline, setHideOffline] = createSignal(false);

  const checkStatus = async () => {
    const list = items();
    if (!list || list.length === 0 || checking()) return;
    setChecking(true);
    setStatuses((prev) => {
      const next = { ...prev };
      for (const c of list) next[c.id] = { state: 'checking' };
      return next;
    });
    try {
      const { statuses: res } = await api.post('/api/connections/status');
      const map = {};
      for (const s of (res || [])) {
        map[s.id] = { state: s.online ? 'online' : 'offline', error: s.error };
      }
      setStatuses(map);
    } catch (e) {
      // Leave the dots "unknown" rather than stuck on "checking".
      setStatuses((prev) => {
        const next = { ...prev };
        for (const c of list) if (next[c.id]?.state === 'checking') next[c.id] = { state: 'unknown' };
        return next;
      });
      toast.error(`Status check failed: ${e.message}`);
    } finally {
      setChecking(false);
    }
  };

  // Auto-probe once, when the connection list first arrives.
  let didInitialCheck = false;
  createEffect(() => {
    const list = items();
    if (list && list.length && !didInitialCheck) {
      didInitialCheck = true;
      checkStatus();
    }
  });

  const offlineCount = createMemo(
    () => (items() || []).filter((c) => statuses()[c.id]?.state === 'offline').length
  );
  // Hide only connections CONFIRMED offline — never unknown/checking/online.
  const matchesOffline = (c) => !hideOffline() || statuses()[c.id]?.state !== 'offline';

  // ── Live search ──
  // Filters on connection name, host, and port (substring, case-insensitive).
  // Applied before grouping, so a group with no matches disappears entirely.
  const [search, setSearch] = createSignal('');
  const searchActive = () => search().trim() !== '';
  const matchesSearch = (c) => {
    const q = search().trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q)
        || (c.host || '').toLowerCase().includes(q)
        || String(c.port || '').includes(q);
  };
  const filteredItems = createMemo(() => (items() || []).filter((c) => matchesSearch(c) && matchesOffline(c)));

  const onSaved = () => {
    setEditing(null);
    refetch();
    props.onChanged?.();
  };

  const test = async (c) => {
    setBusy(c.id);
    try {
      const r = await api.post(`/api/connections/${c.id}/test`);
      if (r.ok) toast.success(`${c.name} — connection OK`);
      else       toast.error(`${c.name} — ${r.error}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const del = async (c) => {
    if (!confirm(`Delete connection "${c.name}"? This cannot be undone.`)) return;
    setBusy(c.id);
    try {
      await api.delete(`/api/connections/${c.id}`);
      toast.success(`Deleted ${c.name}`);
      refetch();
      props.onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Helpers — role checks
  const isReadOnly = () => currentUser()?.role === 'readonly';
  // Admins always may create; a 'user'/'subadmin' needs the per-user flag; readonly never.
  const canCreate  = () => {
    const u = currentUser();
    return u?.role === 'admin' || ((u?.role === 'user' || u?.role === 'subadmin') && !!u?.canCreateConnections);
  };
  const canEdit    = (c) => currentUser()?.role === 'admin' || currentUser()?.id === c.ownerUserId;

  // ── Grouping ──
  // Connections carry an optional free-text `group`. Named groups sort
  // alphabetically; the unnamed bucket ("") renders last as "Ungrouped".
  const groups = createMemo(() => {
    const buckets = new Map(); // key -> connection[]
    for (const c of filteredItems()) {
      const key = (c.group || '').trim();
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(c);
    }
    const named = [...buckets.keys()]
      .filter((k) => k !== '')
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const out = named.map((k) => ({ key: k, label: k, conns: buckets.get(k) }));
    if (buckets.has('')) out.push({ key: '', label: 'Ungrouped', conns: buckets.get('') });
    return out;
  });

  // Only show group headers once at least one connection is actually grouped.
  const hasGroups = createMemo(() => groups().some((g) => g.key !== ''));

  // Distinct group names — fed to the form's datalist for autocomplete. Derived
  // from ALL connections (not the search-filtered set) so suggestions stay
  // complete while a search is active.
  const groupNames = createMemo(() => {
    const set = new Set();
    for (const c of (items() || [])) { const k = (c.group || '').trim(); if (k) set.add(k); }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  });

  // ── Collapse state (persisted per user as a JSON array of group keys) ──
  const collapsedSet = createMemo(() => {
    try {
      const arr = JSON.parse(connectionsCollapsedGroups() || '[]');
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const isCollapsed = (key) => collapsedSet().has(key);
  const toggleCollapse = (key) => {
    const s = new Set(collapsedSet());
    if (s.has(key)) s.delete(key); else s.add(key);
    setConnectionsCollapsedGroups(JSON.stringify([...s]));
  };

  // ── Shared sub-renders (used by both card and list layouts) ──
  // Small reachability dot shown before each connection name.
  const StatusDot = (c) => {
    const s = statuses()[c.id];
    const state = s?.state || 'unknown';
    const title =
      state === 'online'   ? 'Online — reachable'
      : state === 'offline'  ? `Offline — ${s?.error || 'unreachable'}`
      : state === 'checking' ? 'Checking…'
      : 'Status unknown — click Recheck';
    return <span class={`conn-status ${state}`} title={title} />;
  };

  const Badges = (c) => (
    <div class="badges">
      <Show when={c.isShared}><span class="badge shared">Shared</span></Show>
      <Show when={c.readOnly}><span class="badge readonly">Read-only</span></Show>
      <Show when={c.tlsEnabled}><span class="badge tls">TLS</span></Show>
    </div>
  );

  const Actions = (c) => (
    <>
      <button class="btn" onClick={() => props.onOpen?.(c)}>Open</button>
      {/* Test + Duplicate are create/management actions — hidden for readonly
          users. Duplicate isn't gated by canEdit: cloning any visible (incl.
          shared) connection into a new one you own is fine. */}
      <Show when={!isReadOnly()}>
        <button class="btn btn-ghost" disabled={busy() === c.id} onClick={() => test(c)}>
          {busy() === c.id ? 'Testing…' : 'Test'}
        </button>
        <button class="btn btn-ghost" onClick={() => setEditing({ duplicateOf: c })}>Duplicate</button>
      </Show>
      <Show when={canEdit(c)}>
        <button class="btn btn-ghost" onClick={() => setEditing(c)}>Edit</button>
        <button class="btn btn-ghost btn-danger" onClick={() => del(c)}>Delete</button>
      </Show>
    </>
  );

  const Card = (c) => (
    <div class="conn-card" classList={{ 'conn-tinted': !!c.color }} style={c.color ? `--conn-color: ${c.color};` : ''}>
      <header>
        <div>
          <h3>{StatusDot(c)}<Show when={c.color}><span class="conn-swatch" style={`background:${c.color};`} /></Show>{c.name}</h3>
          <div class="conn-meta mono">{c.mysqlUsername}@{c.host}:{c.port}</div>
        </div>
        {Badges(c)}
      </header>
      <Show when={c.defaultDatabase}>
        <div class="conn-default muted">default db: <span class="mono">{c.defaultDatabase}</span></div>
      </Show>
      <footer>{Actions(c)}</footer>
    </div>
  );

  const Row = (c) => (
    <div class="conn-row" classList={{ 'conn-tinted': !!c.color }} style={c.color ? `--conn-color: ${c.color};` : ''}>
      <div class="conn-row-main">
        {StatusDot(c)}
        <Show when={c.color}><span class="conn-swatch" style={`background:${c.color};`} /></Show>
        <span class="conn-row-name">{c.name}</span>
        <span class="conn-meta mono">{c.mysqlUsername}@{c.host}:{c.port}</span>
        <Show when={c.defaultDatabase}>
          <span class="conn-row-db muted mono">{c.defaultDatabase}</span>
        </Show>
        {Badges(c)}
      </div>
      <div class="conn-row-actions">{Actions(c)}</div>
    </div>
  );

  // Renders one bucket of connections in the active layout. Takes an accessor
  // (not a snapshot) so <For> re-reads it reactively after a refetch.
  const ConnSet = (conns) => (
    <Show
      when={connectionsListView()}
      fallback={<div class="conn-grid"><For each={conns()}>{Card}</For></div>}
    >
      <div class="conn-list"><For each={conns()}>{Row}</For></div>
    </Show>
  );

  return (
    <div class="page">
      <header class="page-header">
        <div>
          <h1>Connections</h1>
          <p class="muted">
            {isReadOnly()
              ? 'Connections you have been granted access to.'
              : 'Saved MySQL connections you own or that the team has shared.'}
          </p>
        </div>
        <div class="conn-header-actions">
          <input
            class="input conn-search"
            type="search"
            placeholder="Search name, host, port…"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
          {/* Reachability filter + manual re-probe. */}
          <label class="conn-hide-offline" title="Hide connections that failed the reachability check">
            <input
              type="checkbox"
              checked={hideOffline()}
              onInput={(e) => setHideOffline(e.currentTarget.checked)}
            />
            Hide offline{offlineCount() ? ` (${offlineCount()})` : ''}
          </label>
          <button
            class="btn btn-ghost"
            disabled={checking()}
            onClick={checkStatus}
            title="Re-check reachability of all connections"
          >
            {checking() ? 'Checking…' : '↻ Recheck'}
          </button>
          {/* Card / List view toggle — remembered per user */}
          <div class="view-toggle" role="group" aria-label="View mode">
            <button
              class={`view-toggle-btn ${!connectionsListView() ? 'active' : ''}`}
              title="Card view"
              onClick={() => setConnectionsListView(false)}
            >▦</button>
            <button
              class={`view-toggle-btn ${connectionsListView() ? 'active' : ''}`}
              title="List view"
              onClick={() => setConnectionsListView(true)}
            >☰</button>
          </div>
          {/* Create gated by the per-user can-create-connections flag. */}
          <Show when={canCreate()}>
            <button class="btn" onClick={() => setEditing('new')}>+ New Connection</button>
          </Show>
        </div>
      </header>

      <Show when={items()} fallback={<div class="muted">Loading…</div>}>
        <Show
          when={items().length > 0}
          fallback={
            <div class="empty-state">
              <Show
                when={canCreate()}
                fallback={
                  <>
                    <h2>No connections available</h2>
                    <p class="muted">Ask an admin to grant you access to a connection.</p>
                  </>
                }
              >
                <h2>No connections yet</h2>
                <p class="muted">Create your first MySQL connection to get started.</p>
                <button class="btn" onClick={() => setEditing('new')}>+ New Connection</button>
              </Show>
            </div>
          }
        >
          <Show
            when={filteredItems().length > 0}
            fallback={
              <div class="empty-state">
                <p class="muted">No connections match “{search().trim()}”.</p>
              </div>
            }
          >
            <Show when={hasGroups()} fallback={ConnSet(() => filteredItems())}>
              <For each={groups()}>
                {(g) => (
                  <section class="conn-group">
                    <button class="conn-group-header" onClick={() => toggleCollapse(g.key)}>
                      <span class={`conn-group-chevron ${isCollapsed(g.key) && !searchActive() ? 'collapsed' : ''}`}>▾</span>
                      <span class="conn-group-name">{g.label}</span>
                      <span class="conn-group-count">{g.conns.length}</span>
                    </button>
                    {/* A search auto-expands matching groups so hits are visible. */}
                    <Show when={!isCollapsed(g.key) || searchActive()}>{ConnSet(() => g.conns)}</Show>
                  </section>
                )}
              </For>
            </Show>
          </Show>
        </Show>
      </Show>

      <Show when={editing()}>
        {(ed) => {
          const e = ed();                                  // 'new' | connection | { duplicateOf }
          const dup = (e !== 'new' && e.duplicateOf) || null;
          return (
            <ConnectionForm
              initial={e === 'new' ? null : (dup || e)}
              duplicate={!!dup}
              groups={groupNames()}
              onClose={() => setEditing(null)}
              onSaved={onSaved}
            />
          );
        }}
      </Show>
    </div>
  );
}
